import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { Filter, Plus, X, RotateCcw, Search, Pencil, Hash, ChevronDown, FileText, ToggleLeft, Calendar, Clock, CheckSquare, CircleDot, Tag, Camera, MapPin, PenTool, QrCode, Barcode, Repeat, Star, SlidersHorizontal } from 'lucide-react';
import { SlidePanel } from './SlidePanel';
import { Button } from './Button';
import { formSchemaService } from '../api';

// ── Field type mappings ─────────────────────────────────────────────────────

const NUMERIC_TYPES = ['Number', 'Currency', 'Percentage', 'Rating', 'Slider'];
const STRING_TYPES = ['Text', 'TextArea', 'Email', 'Phone', 'Url', 'QRCode', 'Barcode'];
const SELECT_TYPES = ['Select', 'Radio'];
const BOOLEAN_TYPES = ['Toggle', 'Checkbox'];
const DATE_TYPES = ['Date', 'DateTime'];
const TIME_TYPES = ['Time'];
const JSON_TYPES = ['CheckboxGroup', 'Tags'];
const LAYOUT_TYPES = ['Divider', 'Header', 'Spacer', 'Image', 'Drawing', 'Voice', 'File', 'Signature', 'Location'];
const REPEATED_FORM_TYPE = 'RepeatedForm';

const TYPE_INT_MAP = {
  0: 'Text', 1: 'TextArea', 2: 'Email', 3: 'Phone', 4: 'Url',
  10: 'Number', 11: 'Currency', 12: 'Percentage',
  20: 'Select', 21: 'Radio', 22: 'Checkbox', 23: 'CheckboxGroup', 24: 'Toggle', 25: 'Tags',
  26: 'QRCode', 27: 'Barcode', 30: 'Date', 31: 'Time', 32: 'DateTime',
  40: 'Image', 41: 'Drawing', 42: 'Voice', 43: 'File',
  50: 'Location', 60: 'Divider', 61: 'Header', 62: 'Spacer',
  70: 'Signature', 71: 'Rating', 72: 'Slider', 73: 'RepeatedForm'
};

const resolveType = (t) => typeof t === 'number' ? (TYPE_INT_MAP[t] || 'Text') : t;

const getCategory = (fieldType) => {
  const t = resolveType(fieldType);
  if (NUMERIC_TYPES.includes(t)) return 'numeric';
  if (STRING_TYPES.includes(t)) return 'string';
  if (SELECT_TYPES.includes(t)) return 'select';
  if (BOOLEAN_TYPES.includes(t)) return 'boolean';
  if (TIME_TYPES.includes(t)) return 'time';
  if (DATE_TYPES.includes(t)) return 'date';
  if (JSON_TYPES.includes(t)) return 'json';
  if (t === REPEATED_FORM_TYPE) return 'repeatedForm';
  if (LAYOUT_TYPES.includes(t)) return 'layout';
  return 'string';
};

// ── Field type icons ────────────────────────────────────────────────────────

const FIELD_TYPE_ICONS = {
  Text: Pencil, TextArea: FileText, Email: Pencil, Phone: Pencil, Url: Pencil,
  Number: Hash, Currency: Hash, Percentage: Hash, Rating: Star, Slider: SlidersHorizontal,
  Select: ChevronDown, Radio: CircleDot,
  Checkbox: CheckSquare, CheckboxGroup: CheckSquare, Toggle: ToggleLeft,
  Tags: Tag, QRCode: QrCode, Barcode: Barcode,
  Date: Calendar, Time: Clock, DateTime: Calendar,
  Image: Camera, Drawing: PenTool, Location: MapPin,
  RepeatedForm: Repeat,
};

const getFieldIcon = (fieldType) => {
  const t = resolveType(fieldType);
  return FIELD_TYPE_ICONS[t] || Pencil;
};

const getCategoryLabel = (fieldType) => {
  const cat = getCategory(fieldType);
  return { numeric: 'Number', string: 'Text', select: 'Select', boolean: 'Toggle', date: 'Date', time: 'Time', json: 'Tags' }[cat] || 'Text';
};

// ── Searchable Field Dropdown ───────────────────────────────────────────────

const DropdownWrapper = styled.div`
  position: relative;
  flex: 2;
  min-width: 0;
`;

const DropdownTrigger = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  font-size: 13px;
  text-align: left;
  border: 1px solid ${p => p.$isOpen ? p.theme.colors.tint : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.background};
  color: ${p => p.$hasValue ? p.theme.colors.text : p.theme.colors.muted};
  cursor: pointer;
  min-width: 0;
  transition: border-color 0.15s;

  &:hover { border-color: ${p => p.theme.colors.tint}; }
`;

const TriggerIcon = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: ${p => p.theme.colors.tint};
`;

const TriggerLabel = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TriggerChevron = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: ${p => p.theme.colors.muted};
  transition: transform 0.15s;
  transform: rotate(${p => p.$isOpen ? '180deg' : '0'});
`;

const DropdownPanel = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 100;
  background: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  box-shadow: ${p => p.theme.shadows.lg};
  overflow: hidden;
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const SearchIcon = styled.span`
  display: flex;
  color: ${p => p.theme.colors.muted};
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 13px;
  background: transparent;
  color: ${p => p.theme.colors.text};
  &::placeholder { color: ${p => p.theme.colors.muted}; }
`;

const OptionsList = styled.div`
  max-height: 240px;
  overflow-y: auto;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: ${p => p.theme.colors.border}; border-radius: 4px; }
`;

const OptionItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  font-size: 13px;
  text-align: left;
  border: none;
  background: ${p => p.$isSelected ? p.theme.colors.tint + '15' : 'transparent'};
  color: ${p => p.theme.colors.text};
  cursor: pointer;
  transition: background 0.1s;

  &:hover { background: ${p => p.theme.colors.tint}10; }
`;

const OptionIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.tint}12;
  color: ${p => p.theme.colors.tint};
  flex-shrink: 0;
`;

const OptionLabel = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
`;

const OptionType = styled.span`
  font-size: 11px;
  color: ${p => p.theme.colors.muted};
  flex-shrink: 0;
`;

const NoResults = styled.div`
  padding: 16px;
  text-align: center;
  font-size: 13px;
  color: ${p => p.theme.colors.muted};
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${p => p.theme.colors.tint};
  border-top: 1px solid ${p => p.theme.colors.border};
  margin-top: 4px;
`;

const SearchableFieldSelect = ({ fields, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const selectedField = fields.find(f => f._internalKey === value || f.key === value);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setSearch('');
    }
  }, [isOpen]);

  const filtered = search
    ? fields.filter(f => !f._isGroupHeader && (f.title || f.key).toLowerCase().includes(search.toLowerCase()))
    : fields;

  const handleSelect = (field) => {
    onChange(field._internalKey || field.key, field._repeatedFormSchemaId || null);
    setIsOpen(false);
    setSearch('');
  };

  const SelectedIcon = selectedField ? getFieldIcon(selectedField.type) : null;

  return (
    <DropdownWrapper ref={wrapperRef}>
      <DropdownTrigger
        type="button"
        $isOpen={isOpen}
        $hasValue={!!selectedField}
        onClick={() => setIsOpen(!isOpen)}
      >
        {SelectedIcon && <TriggerIcon><SelectedIcon size={14} /></TriggerIcon>}
        <TriggerLabel>{selectedField ? (selectedField.title || selectedField.key) : 'Select field...'}</TriggerLabel>
        <TriggerChevron $isOpen={isOpen}><ChevronDown size={14} /></TriggerChevron>
      </DropdownTrigger>

      {isOpen && (
        <DropdownPanel>
          <SearchRow>
            <SearchIcon><Search size={14} /></SearchIcon>
            <SearchInput
              ref={inputRef}
              placeholder="Search fields..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </SearchRow>
          <OptionsList>
            {filtered.length === 0 ? (
              <NoResults>No fields match "{search}"</NoResults>
            ) : (
              filtered.map(f => {
                // Group header row (non-selectable)
                if (f._isGroupHeader) {
                  return (
                    <GroupHeader key={f._internalKey}>
                      <Repeat size={12} />
                      {f.title}
                    </GroupHeader>
                  );
                }
                const fKey = f._internalKey || f.key;
                const Icon = getFieldIcon(f.type);
                return (
                  <OptionItem
                    key={fKey}
                    type="button"
                    $isSelected={fKey === value}
                    onClick={() => handleSelect(f)}
                  >
                    <OptionIcon><Icon size={14} /></OptionIcon>
                    <OptionLabel>{f.title || f.key}</OptionLabel>
                    <OptionType>{getCategoryLabel(f.type)}</OptionType>
                  </OptionItem>
                );
              })
            )}
          </OptionsList>
        </DropdownPanel>
      )}
    </DropdownWrapper>
  );
};

// ── Operators per category ──────────────────────────────────────────────────

const OPERATORS = {
  numeric: [
    { label: '=', value: 'equals' },
    { label: '>', value: 'gt' },
    { label: '>=', value: 'gte' },
    { label: '<', value: 'lt' },
    { label: '<=', value: 'lte' },
    { label: 'between', value: 'between' },
    { label: 'is empty', value: 'isNull' },
    { label: 'is not empty', value: 'isNotNull' },
  ],
  string: [
    { label: 'contains', value: 'contains' },
    { label: 'equals', value: 'equals' },
    { label: 'starts with', value: 'startsWith' },
    { label: 'is empty', value: 'isNull' },
    { label: 'is not empty', value: 'isNotNull' },
  ],
  select: [
    { label: 'is', value: 'equals' },
    { label: 'is any of', value: 'in' },
    { label: 'is empty', value: 'isNull' },
    { label: 'is not empty', value: 'isNotNull' },
  ],
  boolean: [
    { label: 'is true', value: 'isTrue' },
    { label: 'is false', value: 'isFalse' },
  ],
  date: [
    { label: 'is', value: 'equals' },
    { label: 'before', value: 'before' },
    { label: 'after', value: 'after' },
    { label: 'between', value: 'between' },
    { label: 'is empty', value: 'isNull' },
    { label: 'is not empty', value: 'isNotNull' },
  ],
  time: [
    { label: 'is', value: 'equals' },
    { label: 'before', value: 'before' },
    { label: 'after', value: 'after' },
    { label: 'between', value: 'between' },
    { label: 'is empty', value: 'isNull' },
    { label: 'is not empty', value: 'isNotNull' },
  ],
  json: [
    { label: 'contains', value: 'contains' },
    { label: 'is empty', value: 'isNull' },
    { label: 'is not empty', value: 'isNotNull' },
  ],
};

const NO_VALUE_OPS = ['isNull', 'isNotNull', 'isTrue', 'isFalse'];

// ── Styled Components ───────────────────────────────────────────────────────

const PanelBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.sm};
  padding-bottom: ${p => p.theme.spacing.md};
`;

const LogicRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  padding: ${p => p.theme.spacing.md};
  background: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  margin-bottom: ${p => p.theme.spacing.sm};
`;

const LogicLabel = styled.span`
  font-size: ${p => p.theme.typography.sizes.small};
  color: ${p => p.theme.colors.text};
  font-weight: 500;
`;

const LogicSelect = styled.select`
  padding: 4px 8px;
  font-size: ${p => p.theme.typography.sizes.small};
  font-weight: 600;
  border: 1px solid ${p => p.theme.colors.tint};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.tint}12;
  color: ${p => p.theme.colors.tint};
  cursor: pointer;
  &:focus { outline: none; }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px 0;
`;

const DividerLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${p => p.theme.colors.muted};
  background: ${p => p.theme.colors.background};
  padding: 0 ${p => p.theme.spacing.sm};
`;

const RuleCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.sm};
  padding: ${p => p.theme.spacing.md};
  background: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  position: relative;
`;

const RuleRow = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
  align-items: center;
`;

const RuleSelect = styled.select`
  padding: 7px 10px;
  font-size: 13px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
  cursor: pointer;
  min-width: 0;
  &:focus { outline: none; border-color: ${p => p.theme.colors.tint}; }
`;

const OperatorSelect = styled(RuleSelect)`
  flex: 1;
  min-width: 110px;
`;

const ValueInput = styled.input`
  flex: 1;
  padding: 7px 10px;
  font-size: 13px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
  min-width: 0;
  &:focus { outline: none; border-color: ${p => p.theme.colors.tint}; }
  &::placeholder { color: ${p => p.theme.colors.muted}; }
`;

const RemoveBtn = styled.button`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 1px solid transparent;
  border-radius: ${p => p.theme.borderRadius.md};
  color: ${p => p.theme.colors.muted};
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;
  &:hover { border-color: ${p => p.theme.colors.danger}; color: ${p => p.theme.colors.danger}; }
`;

const RangeLabel = styled.span`
  font-size: 12px;
  color: ${p => p.theme.colors.muted};
  flex-shrink: 0;
`;

const OptionChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const OptionChip = styled.button`
  padding: 4px 12px;
  font-size: 12px;
  border: 1px solid ${p => p.$selected ? p.theme.colors.tint : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.round};
  background: ${p => p.$selected ? p.theme.colors.tint + '18' : 'transparent'};
  color: ${p => p.$selected ? p.theme.colors.tint : p.theme.colors.text};
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: ${p => p.theme.colors.tint}; }
`;

const AddBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 10px;
  font-size: 13px;
  font-weight: 500;
  color: ${p => p.theme.colors.tint};
  background: ${p => p.theme.colors.tint}08;
  border: 1px dashed ${p => p.theme.colors.tint}40;
  border-radius: ${p => p.theme.borderRadius.lg};
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: ${p => p.theme.colors.tint}15; border-color: ${p => p.theme.colors.tint}; }
`;

const EmptyFilterWarning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${p => p.theme.spacing.sm};
  margin: 0 ${p => p.theme.spacing.lg};
  padding: ${p => p.theme.spacing.md};
  background: #fff8ec;
  border-left: 3px solid #f59e0b;
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: 0.8125rem;
  color: #92400e;
`;

const EmptyFilterWarningIcon = styled.div`
  flex-shrink: 0;
  margin-top: 1px;
  color: #f59e0b;
`;

const EmptyFilterWarningBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  strong { font-weight: 600; }
  span { opacity: 0.85; }
`;

const FooterBar = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
  padding: ${p => p.theme.spacing.lg};
  border-top: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.backgroundSecondary};
`;

const EmptyHint = styled.div`
  text-align: center;
  padding: ${p => p.theme.spacing.xl} ${p => p.theme.spacing.md};
  color: ${p => p.theme.colors.muted};
  font-size: ${p => p.theme.typography.sizes.small};
`;

const BooleanToggleRow = styled.div`
  display: flex;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  overflow: hidden;
  flex-shrink: 0;
`;

const BooleanBtn = styled.button`
  padding: 6px 14px;
  font-size: 12px;
  font-weight: ${p => p.$active ? '600' : '400'};
  border: none;
  background: ${p => p.$active ? p.theme.colors.tint + '18' : 'transparent'};
  color: ${p => p.$active ? p.theme.colors.tint : p.theme.colors.muted};
  cursor: pointer;
  transition: all 0.15s;
  &:not(:last-child) { border-right: 1px solid ${p => p.theme.colors.border}; }
  &:hover { background: ${p => p.theme.colors.tint}10; }
`;

const DatePresetsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const PresetChip = styled.button`
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 500;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.round};
  background: transparent;
  color: ${p => p.theme.colors.muted};
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: ${p => p.theme.colors.tint}; color: ${p => p.theme.colors.tint}; background: ${p => p.theme.colors.tint}08; }
`;

// ── Date Presets ────────────────────────────────────────────────────────────

const toDateStr = (d) => d.toISOString().split('T')[0];

// Each preset sets both operator and value(s) — no need to pick operator first
const DATE_PRESETS = [
  { label: 'Today', apply: () => ({ operator: 'between', value: toDateStr(new Date()), valueTo: toDateStr(new Date()) }) },
  { label: 'Yesterday', apply: () => { const d = new Date(); d.setDate(d.getDate() - 1); const s = toDateStr(d); return { operator: 'between', value: s, valueTo: s }; } },
  { label: 'Last 7 Days', apply: () => { const to = new Date(); const from = new Date(); from.setDate(from.getDate() - 7); return { operator: 'between', value: toDateStr(from), valueTo: toDateStr(to) }; } },
  { label: 'Last 30 Days', apply: () => { const to = new Date(); const from = new Date(); from.setDate(from.getDate() - 30); return { operator: 'between', value: toDateStr(from), valueTo: toDateStr(to) }; } },
  { label: 'This Month', apply: () => { const now = new Date(); return { operator: 'after', value: toDateStr(new Date(now.getFullYear(), now.getMonth(), 1)) }; } },
  { label: 'Last Month', apply: () => { const now = new Date(); return { operator: 'between', value: toDateStr(new Date(now.getFullYear(), now.getMonth() - 1, 1)), valueTo: toDateStr(new Date(now.getFullYear(), now.getMonth(), 0)) }; } },
  { label: 'This Year', apply: () => { const now = new Date(); return { operator: 'after', value: toDateStr(new Date(now.getFullYear(), 0, 1)) }; } },
];

// ── Helpers ─────────────────────────────────────────────────────────────────

let _ruleId = 0;
const nextId = () => `rule_${++_ruleId}`;

const emptyRule = () => ({ id: nextId(), fieldKey: '', operator: '', value: '', valueTo: '', values: [], repeatedFormSchemaId: null });

const getFieldOptions = (field) => {
  const opts = field?.configuration?.options;
  if (!opts?.length) return [];
  return opts.map(o => typeof o === 'string' ? { value: o, label: o } : { value: o.value || o.label, label: o.label || o.value });
};

// ── Component ───────────────────────────────────────────────────────────────

export const FilterPanel = ({ isOpen, onClose, schemaFields = [], filters = [], filterLogic: externalLogic = 'and', onFiltersChange, onFilterLogicChange }) => {
  const [rules, setRules] = useState([]);
  const [logic, setLogic] = useState('and');
  const [showEmptyWarning, setShowEmptyWarning] = useState(false);
  const [childSchemas, setChildSchemas] = useState({}); // { [repeatedFormSchemaId]: { name, fields: [...] } }
  const [childSchemasLoaded, setChildSchemasLoaded] = useState(new Set());

  // Identify repeated form fields in the schema
  const repeatedFormFields = useMemo(
    () => schemaFields.filter(f => resolveType(f.type) === REPEATED_FORM_TYPE && f.configuration?.repeatedFormSchemaId),
    [schemaFields]
  );

  // Load child schemas when panel opens
  useEffect(() => {
    if (!isOpen) return;
    const toFetch = repeatedFormFields.filter(rf => !childSchemasLoaded.has(rf.configuration.repeatedFormSchemaId));
    if (toFetch.length === 0) return;

    setChildSchemasLoaded(prev => {
      const next = new Set(prev);
      toFetch.forEach(rf => next.add(rf.configuration.repeatedFormSchemaId));
      return next;
    });

    toFetch.forEach(async (rfField) => {
      try {
        const schema = await formSchemaService.getFormSchemaById(rfField.configuration.repeatedFormSchemaId);
        let definition = schema?.schemaDefinition || schema;
        if (typeof definition === 'string') definition = JSON.parse(definition);
        let fieldsArray = definition?.fields;
        if (fieldsArray && !Array.isArray(fieldsArray) && fieldsArray.$values) fieldsArray = fieldsArray.$values;

        if (Array.isArray(fieldsArray)) {
          const childFields = fieldsArray
            .filter(f => f.key && getCategory(f.type) !== 'layout' && resolveType(f.type) !== REPEATED_FORM_TYPE)
            .map(f => ({
              key: f.key,
              title: f.title || f.key,
              type: resolveType(f.type),
              configuration: f.config || f.configuration || {},
            }));
          setChildSchemas(prev => ({
            ...prev,
            [rfField.configuration.repeatedFormSchemaId]: {
              name: schema?.name || rfField.title || 'Repeated Form',
              parentFieldKey: rfField.key,
              fields: childFields,
            }
          }));
        }
      } catch (err) {
        console.error(`Failed to fetch child schema for ${rfField.key}:`, err);
      }
    });
  }, [isOpen, repeatedFormFields, childSchemasLoaded]);

  // Build enriched filterable fields: parent fields + grouped child fields.
  // Lab-integration fields (those with labResultSource) get their own "🧪 Lab Results"
  // group at the end so they don't mix with user-entered fields in the operator list.
  const filterableFields = useMemo(() => {
    const parentFields = schemaFields.filter(f => {
      const cat = getCategory(f.type);
      return cat !== 'layout' && cat !== 'repeatedForm';
    });

    const regularParents = parentFields.filter(f => !f.labResultSource);
    const labParents = parentFields.filter(f => f.labResultSource);

    const grouped = [...regularParents];
    for (const rfField of repeatedFormFields) {
      const schemaId = rfField.configuration.repeatedFormSchemaId;
      const childData = childSchemas[schemaId];
      if (!childData?.fields?.length) continue;

      // Add group header
      grouped.push({
        _isGroupHeader: true,
        _internalKey: `_gh_${schemaId}`,
        title: childData.name,
      });

      // Add child fields with composite keys
      for (const cf of childData.fields) {
        grouped.push({
          ...cf,
          _internalKey: `rf:${schemaId}:${cf.key}`,
          _repeatedFormSchemaId: schemaId,
          _parentFieldTitle: childData.name,
        });
      }
    }

    // Lab-integration group — only render when the form actually has lab fields.
    if (labParents.length > 0) {
      grouped.push({
        _isGroupHeader: true,
        _internalKey: '_gh_lab_results',
        title: '🧪 Lab Results',
      });
      for (const lf of labParents) {
        grouped.push(lf);
      }
    }

    return grouped;
  }, [schemaFields, repeatedFormFields, childSchemas]);

  // Hydrate rules from external filters when panel opens
  useEffect(() => {
    if (!isOpen) return;
    setLogic(externalLogic || 'and');
    if (filters.length > 0) {
      setRules(filters.map(f => {
        const rfSchemaId = f.repeatedFormSchemaId || null;
        // For child fields, reconstruct the composite key for the dropdown
        const internalKey = rfSchemaId ? `rf:${rfSchemaId}:${f.fieldKey}` : f.fieldKey;
        return {
          id: nextId(),
          fieldKey: internalKey,
          operator: mapBackendOpToLocal(f, schemaFields, childSchemas),
          value: f.value || '',
          valueTo: f.valueTo || '',
          values: f.values || [],
          repeatedFormSchemaId: rfSchemaId,
        };
      }));
    } else {
      setRules([]);
    }
  }, [isOpen]);

  const addRule = useCallback(() => { setShowEmptyWarning(false); setRules(prev => [...prev, emptyRule()]); }, []);
  const removeRule = useCallback((id) => setRules(prev => prev.filter(r => r.id !== id)), []);

  const updateRule = useCallback((id, updates) => {
    setRules(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
  }, []);

  const clearAll = useCallback(() => {
    setRules([]);
    onFiltersChange([], logic);
    onClose();
  }, [logic, onFiltersChange, onClose]);

  const applyFilters = useCallback(() => {
    const result = [];
    for (const rule of rules) {
      if (!rule.fieldKey || !rule.operator) continue;
      const dtos = localRuleToDtos(rule);
      result.push(...dtos);
    }
    if (result.length === 0) {
      setShowEmptyWarning(true);
      return;
    }
    setShowEmptyWarning(false);
    onFiltersChange(result, logic);
    if (onFilterLogicChange) onFilterLogicChange(logic);
    onClose();
  }, [rules, logic, onFiltersChange, onFilterLogicChange, onClose]);

  return (
    <SlidePanel isOpen={isOpen} onClose={onClose} title="Filter Submissions" width="520px">
      <PanelBody>
        {filterableFields.length === 0 ? (
          <EmptyHint>No filterable fields in this form.</EmptyHint>
        ) : (
          <>
            {rules.length > 1 && (
              <LogicRow>
                <LogicLabel>Match</LogicLabel>
                <LogicSelect value={logic} onChange={e => setLogic(e.target.value)}>
                  <option value="and">ALL</option>
                  <option value="or">ANY</option>
                </LogicSelect>
                <LogicLabel>of the following</LogicLabel>
              </LogicRow>
            )}

            {rules.map((rule, idx) => {
              const field = filterableFields.find(f => (f._internalKey || f.key) === rule.fieldKey);
              const category = field ? getCategory(field.type) : null;
              const ops = category ? (OPERATORS[category] || []) : [];
              const needsValue = !NO_VALUE_OPS.includes(rule.operator);
              const isBetween = rule.operator === 'between';
              const isIn = rule.operator === 'in';
              const fieldOpts = field ? getFieldOptions(field) : [];

              return (
                <React.Fragment key={rule.id}>
                  {idx > 0 && (
                    <Divider>
                      <DividerLabel>{logic === 'and' ? 'AND' : 'OR'}</DividerLabel>
                    </Divider>
                  )}
                  <RuleCard>
                    <RuleRow>
                      <SearchableFieldSelect
                        fields={filterableFields}
                        value={rule.fieldKey}
                        onChange={(fieldKey, repeatedFormSchemaId) => updateRule(rule.id, {
                          fieldKey, repeatedFormSchemaId: repeatedFormSchemaId || null,
                          operator: '', value: '', valueTo: '', values: []
                        })}
                      />

                      {/* Boolean: inline Yes/No/Any buttons instead of operator dropdown */}
                      {rule.fieldKey && category === 'boolean' && (
                        <BooleanToggleRow>
                          {[
                            { label: 'Any', op: '' },
                            { label: 'Yes', op: 'isTrue' },
                            { label: 'No', op: 'isFalse' },
                          ].map(opt => (
                            <BooleanBtn key={opt.op} $active={rule.operator === opt.op} onClick={() => updateRule(rule.id, { operator: opt.op })}>
                              {opt.label}
                            </BooleanBtn>
                          ))}
                        </BooleanToggleRow>
                      )}

                      {/* Non-boolean: operator dropdown */}
                      {rule.fieldKey && category !== 'boolean' && (
                        <OperatorSelect
                          value={rule.operator}
                          onChange={e => updateRule(rule.id, { operator: e.target.value, value: '', valueTo: '', values: [] })}
                        >
                          <option value="">Operator...</option>
                          {ops.map(op => (
                            <option key={op.value} value={op.value}>{op.label}</option>
                          ))}
                        </OperatorSelect>
                      )}

                      <RemoveBtn onClick={() => removeRule(rule.id)} title="Remove filter">
                        <X size={14} />
                      </RemoveBtn>
                    </RuleRow>

                    {/* Date presets — show immediately when date field is selected */}
                    {rule.fieldKey && category === 'date' && (
                      <DatePresetsRow>
                        {DATE_PRESETS.map(preset => (
                          <PresetChip key={preset.label} onClick={() => {
                            const result = preset.apply();
                            updateRule(rule.id, { operator: result.operator, value: result.value, valueTo: result.valueTo || '' });
                          }}>
                            {preset.label}
                          </PresetChip>
                        ))}
                      </DatePresetsRow>
                    )}

                    {/* Value input row — adapts to operator (skip if chips are shown instead) */}
                    {rule.fieldKey && rule.operator && needsValue && !isIn && category !== 'boolean'
                      && !(category === 'json' && rule.operator === 'contains' && fieldOpts.length > 0)
                      && !(category === 'select' && rule.operator === 'equals' && fieldOpts.length > 0) && (
                      <RuleRow>
                        <ValueInput
                          type={category === 'numeric' ? 'number' : category === 'date' ? 'date' : category === 'time' ? 'time' : 'text'}
                          placeholder="Value..."
                          value={rule.value}
                          onChange={e => updateRule(rule.id, { value: e.target.value })}
                        />
                        {isBetween && (
                          <>
                            <RangeLabel>to</RangeLabel>
                            <ValueInput
                              type={category === 'numeric' ? 'number' : category === 'date' ? 'date' : category === 'time' ? 'time' : 'text'}
                              placeholder="Value..."
                              value={rule.valueTo}
                              onChange={e => updateRule(rule.id, { valueTo: e.target.value })}
                            />
                          </>
                        )}
                      </RuleRow>
                    )}

                    {/* Single-select chips for "is" (Select fields) */}
                    {rule.fieldKey && category === 'select' && rule.operator === 'equals' && fieldOpts.length > 0 && (
                      <OptionChipsContainer>
                        {fieldOpts.map(opt => {
                          const selected = rule.value === opt.value;
                          return (
                            <OptionChip key={opt.value} $selected={selected}
                              onClick={() => updateRule(rule.id, { value: selected ? '' : opt.value })}
                            >{opt.label}</OptionChip>
                          );
                        })}
                      </OptionChipsContainer>
                    )}

                    {/* Multi-select chips for "is any of" (Select fields) */}
                    {rule.fieldKey && isIn && fieldOpts.length > 0 && (
                      <OptionChipsContainer>
                        {fieldOpts.map(opt => {
                          const selected = (rule.values || []).includes(opt.value);
                          return (
                            <OptionChip key={opt.value} $selected={selected}
                              onClick={() => {
                                const next = selected ? rule.values.filter(v => v !== opt.value) : [...(rule.values || []), opt.value];
                                updateRule(rule.id, { values: next });
                              }}>{opt.label}</OptionChip>
                          );
                        })}
                      </OptionChipsContainer>
                    )}

                    {/* CheckboxGroup/Tags: multi-select chips when "contains" + has options */}
                    {rule.fieldKey && category === 'json' && rule.operator === 'contains' && fieldOpts.length > 0 && (
                      <OptionChipsContainer>
                        {fieldOpts.map(opt => {
                          // Multi-toggle: values stored as comma-joined in rule.value
                          const selectedVals = (rule.value || '').split(',').map(v => v.trim()).filter(Boolean);
                          const selected = selectedVals.includes(opt.value);
                          return (
                            <OptionChip key={opt.value} $selected={selected}
                              onClick={() => {
                                const next = selected
                                  ? selectedVals.filter(v => v !== opt.value)
                                  : [...selectedVals, opt.value];
                                updateRule(rule.id, { value: next.join(',') });
                              }}>{opt.label}</OptionChip>
                          );
                        })}
                      </OptionChipsContainer>
                    )}

                    {/* CheckboxGroup/Tags: text fallback when no options */}
                    {rule.fieldKey && category === 'json' && rule.operator === 'contains' && fieldOpts.length === 0 && (
                      <RuleRow>
                        <ValueInput type="text" placeholder="Value to search for..." value={rule.value}
                          onChange={e => updateRule(rule.id, { value: e.target.value })} />
                      </RuleRow>
                    )}

                    {/* Text fallback for "is any of" with no known options */}
                    {rule.fieldKey && isIn && fieldOpts.length === 0 && (
                      <RuleRow>
                        <ValueInput type="text" placeholder="Comma-separated values..."
                          value={(rule.values || []).join(', ')}
                          onChange={e => updateRule(rule.id, { values: e.target.value.split(',').map(v => v.trim()).filter(Boolean) })} />
                      </RuleRow>
                    )}
                  </RuleCard>
                </React.Fragment>
              );
            })}

            <AddBtn onClick={addRule}>
              <Plus size={14} /> Add Filter
            </AddBtn>
          </>
        )}
      </PanelBody>

      {showEmptyWarning && (
        <EmptyFilterWarning>
          <EmptyFilterWarningIcon><Filter size={16} /></EmptyFilterWarningIcon>
          <EmptyFilterWarningBody>
            <strong>No filters added yet.</strong>
            <span>Add at least one filter condition before applying, or close the panel to view all submissions.</span>
          </EmptyFilterWarningBody>
          <button onClick={() => setShowEmptyWarning(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'inherit', opacity: 0.6 }}>
            <X size={14} />
          </button>
        </EmptyFilterWarning>
      )}
      <FooterBar>
        <Button variant="secondary" size="sm" onClick={clearAll} style={{ flex: 1 }}>
          <RotateCcw size={14} /> Clear All
        </Button>
        <Button variant="primary" size="sm" onClick={applyFilters} style={{ flex: 2 }}>
          <Filter size={14} /> Apply Filters
        </Button>
      </FooterBar>
    </SlidePanel>
  );
};

// ── Converters ──────────────────────────────────────────────────────────────

/** Convert local rule state to backend FieldFilterDto(s) — returns array */
function localRuleToDtos(rule) {
  const { fieldKey: rawKey, operator, value, valueTo, values, repeatedFormSchemaId } = rule;
  if (!rawKey || !operator) return [];

  // Extract actual field key from composite key (rf:{schemaId}:{childKey} → childKey)
  const fieldKey = rawKey.startsWith('rf:') ? rawKey.split(':').slice(2).join(':') : rawKey;

  // Build base DTO with optional repeatedFormSchemaId
  const baseDto = (dto) => {
    if (repeatedFormSchemaId) dto.repeatedFormSchemaId = repeatedFormSchemaId;
    return dto;
  };

  // Boolean pseudo-operators → backend "equals"
  if (operator === 'isTrue') return [baseDto({ fieldKey, operator: 'equals', value: 'true' })];
  if (operator === 'isFalse') return [baseDto({ fieldKey, operator: 'equals', value: 'false' })];

  // No-value operators
  if (NO_VALUE_OPS.includes(operator)) return [baseDto({ fieldKey, operator })];

  // "in" with values array
  if (operator === 'in') {
    if (!values?.length) return [];
    return [baseDto({ fieldKey, operator, values })];
  }

  // "contains" with comma-separated multi-values (CheckboxGroup multi-select)
  if (operator === 'contains' && value && value.includes(',')) {
    return value.split(',').map(v => v.trim()).filter(Boolean).map(v => baseDto({ fieldKey, operator, value: v }));
  }

  // "between" with two values
  if (operator === 'between') {
    if (!value && !valueTo) return [];
    const dto = { fieldKey, operator };
    if (value) dto.value = value;
    if (valueTo) dto.valueTo = valueTo;
    return [baseDto(dto)];
  }

  // Standard single-value operators
  if (!value) return [];
  return [baseDto({ fieldKey, operator, value })];
}

/** Convert backend FieldFilterDto to local rule operator for hydration */
function mapBackendOpToLocal(filter, schemaFields, childSchemas = {}) {
  let field = schemaFields.find(f => f.key === filter.fieldKey);

  // For repeated form filters, look up the field in the child schema
  if (!field && filter.repeatedFormSchemaId && childSchemas[filter.repeatedFormSchemaId]) {
    field = childSchemas[filter.repeatedFormSchemaId].fields?.find(f => f.key === filter.fieldKey);
  }

  const cat = field ? getCategory(field.type) : 'string';

  // Boolean: backend sends "equals" with value "true"/"false"
  if (cat === 'boolean') {
    if (filter.operator === 'equals' && filter.value === 'true') return 'isTrue';
    if (filter.operator === 'equals' && filter.value === 'false') return 'isFalse';
  }

  return filter.operator;
}

export default FilterPanel;
